import { get, set, startCase } from 'lodash-es';
import { numberWithCommas } from 'site-modules/shared/utils/string';
import { getPriceStringV2 } from 'site-modules/shared/utils/price-utils';
import { PRIVATE_PARTY_VALUE, TRADE_IN_VALUE } from 'site-modules/shared/constants/appraisal/appraisal';

/* BLOCK OF PRIVATE FUNCTIONS */
/**
 * Collect trade in and private party price values according to condition.
 * @param usedTmvValues
 * @returns {Object}
 */
function getAllTmvBendsPrices(usedTmvValues) {
  return Object.entries(usedTmvValues).reduce((result, [condition, values]) => {
    const { usedTradeIn, usedPrivateParty } = get(values, 'Current.totalWithOptions', {});
    const startCaseCondition = startCase(condition.toLowerCase());

    set(result, `${PRIVATE_PARTY_VALUE}${startCaseCondition}`, getPriceStringV2(usedPrivateParty));
    set(result, `${TRADE_IN_VALUE}${startCaseCondition}`, getPriceStringV2(usedTradeIn));

    return result;
  }, {});
}

/**
 * Get prices, transform it and set to result with provided prefix in name.
 * @param {String} pricePrefix
 * @param {Object} prices
 * @returns {Object}
 */
function getPrices(pricePrefix, prices) {
  return {
    [`${pricePrefix}TradeIn`]: numberWithCommas(get(prices, 'tradeIn', 0)),
    [`${pricePrefix}PrivateParty`]: numberWithCommas(get(prices, 'privateParty', 0)),
    [`${pricePrefix}TmvRetail`]: numberWithCommas(get(prices, 'dealerRetail', 0)),
  };
}

/**
 * Transforms Appraisal Email Data to the email values
 * @param appraisalEmailData
 * @returns {Object}
 */
const transformAppraisalEmailData = appraisalEmailData => {
  const {
    tmvConditions,
    colorPrices,
    conditionPrices,
    mileagePrices,
    nationalPrices,
    optionalPrices,
    regionalPrices,
    totalPrices,
    certifiedUsedPrice,
  } = appraisalEmailData;
  return {
    ...getAllTmvBendsPrices(tmvConditions),
    ...getPrices('colorAdj', colorPrices),
    ...getPrices('condition', conditionPrices),
    ...getPrices('mileage', mileagePrices),
    ...getPrices('nationalBase', nationalPrices),
    ...getPrices('totalOptional', optionalPrices),
    ...getPrices('regional', regionalPrices),
    ...getPrices('total', totalPrices),
    certifiedUsedPrice,
  };
};

/**
 * Transforms Partner Offer Email Data to the email values
 * @param partnerOfferEmailData
 * @returns {Object}
 */
const transformPartnerOfferEmailData = partnerOfferEmailData => {
  const {
    offerVin,
    offerCode,
    offerExpiredDate,
    offerExpiredTs,
    offerStoreName,
    offerStoreAddressLine1,
    offerStoreAddressLine2,
    estimatedValues,
  } = partnerOfferEmailData || {};
  return {
    offerVin,
    offerCode,
    offerExpiredDate,
    offerExpiredTs,
    offerStoreName,
    offerStoreAddressLine1,
    offerStoreAddressLine2,
    estimatedValues,
  };
};

/**
 * Transforms Vehicle Data to the email values
 * @param vehicle
 * @returns {object}
 */
const transformVehicleData = vehicle => {
  const {
    style: { year, makeName, modelName, name: submodelName, trimName: trim, makeSlug, modelSlug, id },
  } = vehicle;
  const submodelNameSection = (submodelName && ` ${submodelName}`) || '';

  return {
    vehicleFullName: `${year} ${makeName} ${modelName}${submodelNameSection}`,
    make: makeName,
    model: modelName,
    year,
    trim,
    makeSlug,
    modelSlug,
    styleId: id,
  };
};
/* END OF BLOCK OF PRIVATE FUNCTIONS */

/* PUBLIC FUNCTIONS */
/**
 * Collect all data for appraisal email template.
 * @param {String} mileage
 * @param {String} colorName
 * @param {String} condition
 * @param {Number} zipCode
 * @param {Object} vehicle
 * @param {Object} appraisalEmailData
 * @param {Object} partnerOfferEmailData
 * @returns {Object}
 * */
export function collectAppraisalEmailValues({
  mileage,
  exteriorColor,
  condition,
  zipCode,
  vehicle,
  appraisalEmailData,
  partnerOfferEmailData,
}) {
  return {
    ...transformAppraisalEmailData(appraisalEmailData),
    ...transformVehicleData(vehicle),
    ...transformPartnerOfferEmailData(partnerOfferEmailData),
    color: exteriorColor?.name ?? '',
    condition,
    selectedCondition: condition && condition.toLowerCase(),
    mileage,
    zip: zipCode,
  };
}
